import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useConfig from 'hooks/useConfig';
import Transitions from 'components/assets/Transitions';
import { updateUser } from 'features/eai/thunks/userThunks';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { selectAccountInfo } from 'features/account/accountSelectors';
import { useKeycloak } from 'contexts/KeycloakContext';
import { accountLogin } from 'features/account/accountSlice';
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  ClickAwayListener,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Translation } from 'models/i18n';
import { UpdateUserRequestBody } from 'features/eai/eaiTypes';

interface Locale {
  key: string;
  name: string;
}

// ==============================|| LOCALIZATION ||============================== //

const LocalizationSection = () => {
  const { borderRadius, locale } = useConfig();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();

  const { breakpoints, typography, palette } = useTheme();
  const matchesMd = useMediaQuery(breakpoints.down('md'));
  const anchorRef = useRef<any>(null);

  const [open, setOpen] = useState<boolean>(false);

  const localizationList: Locale[] = useMemo(
    () =>
      Object.values(Translation).map((localeKey) => ({
        key: localeKey,
        name: t(`header.localization.${localeKey}`)
      })),
    [t]
  );

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const loadAndSetUserInfo = () => {
    keycloak?.loadUserInfo().then((info) => {
      dispatch(
        accountLogin({
          accessToken: keycloak.token,
          accountInfo: {
            session_state: keycloak.tokenParsed?.session_state,
            ...info
          }
        })
      );
    });
  };

  const { sub } = useAppSelector(selectAccountInfo) ?? {};

  const handleListItemClick = (
    _event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | undefined,
    lng: string
  ) => {
    i18n.changeLanguage(lng).then(() => {
      if (sub) {
        const requestBody: Partial<UpdateUserRequestBody> = {
          locale: lng
        };

        dispatch(
          updateUser(requestBody, sub, () => {
            loadAndSetUserInfo();
          })
        );
      }

      setOpen(false);
    });
  };

  const handleToggle = () => {
    setOpen((previousValue) => !previousValue);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;

    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          ml: 2,
          [breakpoints.down('md')]: {
            ml: 1
          }
        }}
      >
        <Avatar
          variant='rounded'
          sx={{
            ...typography.commonAvatar,
            ...typography.mediumAvatar,
            border: '1px solid',
            borderColor:
              palette.mode === 'dark'
                ? palette.dark.main
                : palette.primary.light,
            background:
              palette.mode === 'dark'
                ? palette.dark.main
                : palette.primary.light,
            color: palette.primary.dark,
            transition: 'all .2s ease-in-out',
            '&[aria-controls="menu-list-grow"],&:hover': {
              borderColor: palette.primary.main,
              background: palette.primary.main,
              color: palette.primary.light
            }
          }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup='true'
          onClick={handleToggle}
          color='inherit'
        >
          <Typography
            variant='h5'
            sx={{ textTransform: 'uppercase' }}
            color='inherit'
          >
            {locale}
          </Typography>
        </Avatar>
      </Box>

      <Popper
        placement={matchesMd ? 'bottom-start' : 'bottom'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 20]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions
              position={matchesMd ? 'top-left' : 'top'}
              in={open}
              {...TransitionProps}
            >
              <Paper elevation={16}>
                {open && (
                  <List
                    component='nav'
                    sx={{
                      width: '100%',
                      minWidth: 200,
                      maxWidth: 280,
                      bgcolor: palette.background.paper,
                      borderRadius: `${borderRadius}px`,
                      [breakpoints.down('md')]: {
                        maxWidth: 250
                      }
                    }}
                  >
                    {localizationList.map(({ key, name }) => (
                      <ListItemButton
                        selected={locale === key}
                        onClick={(event) => handleListItemClick(event, key)}
                        key={key}
                      >
                        <ListItemText
                          primary={
                            <Grid container>
                              <Typography color='textPrimary'>
                                {name}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='textSecondary'
                                sx={{ ml: '8px', textTransform: 'uppercase' }}
                              >
                                ({key})
                              </Typography>
                            </Grid>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default LocalizationSection;
